import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useNotifications } from '../lib/NotificationsContext';
import { Breadcrumbs } from './Breadcrumbs';
import { cn } from '../lib/utils';
import { LanguageSelector } from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import { 
  Bell, 
  Volume2, 
  VolumeX, 
  Check, 
  CheckCheck, 
  Trash2,
  X,
  Trophy,
  Search,
  Palette,
  ShoppingBag,
  FileText,
  Menu
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Get icon for notification type
function getNotificationIcon(phase) {
  const icons = {
    'product_reviews': Search,
    'product_marketing_angles': FileText,
    'ugc_ads': Palette,
    'ugc_full_videos': Palette,
    'ugc_shorts': Palette,
    'winning_product_details': Trophy,
    'winning_product_images': Trophy,
    'static_ads': Palette,
    'shopify': ShoppingBag,
    'shopify_product_images': ShoppingBag,
    'shopify_sections': ShoppingBag
  };
  return icons[phase] || Bell;
}

// Get color for notification type
function getNotificationColor(phase) {
  const colors = {
    'product_reviews': 'bg-blue-100 text-blue-600',
    'product_marketing_angles': 'bg-violet-100 text-violet-600',
    'ugc_ads': 'bg-purple-100 text-purple-600',
    'ugc_full_videos': 'bg-purple-100 text-purple-600',
    'ugc_shorts': 'bg-purple-100 text-purple-600',
    'winning_product_details': 'bg-amber-100 text-amber-600',
    'winning_product_images': 'bg-amber-100 text-amber-600',
    'static_ads': 'bg-orange-100 text-orange-600',
    'shopify': 'bg-green-100 text-green-600',
    'shopify_product_images': 'bg-green-100 text-green-600',
    'shopify_sections': 'bg-green-100 text-green-600'
  };
  return colors[phase] || 'bg-slate-100 text-slate-600';
}

export function Header({ productName, collapsed, isMobile, onMenuClick }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    notifications, 
    unreadCount, 
    soundEnabled, 
    toggleSound,
    markAsRead,
    markAllAsRead,
    clearAll
  } = useNotifications();
  
  const [showNotifications, setShowNotifications] = useState(false);
  const panelRef = useRef(null);
  const { t } = useTranslation();

  // Get user initials
  const getInitials = () => {
    if (!user?.email) return 'U';
    const parts = user.email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.email.slice(0, 2).toUpperCase();
  };

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header 
      className={cn(
        'h-[65px] bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6',
        'sticky top-0 z-40'
      )}
      data-testid="header"
    >
      {/* Left: Logo (mobile) or Breadcrumbs (desktop) */}
      <div className="flex-1 flex items-center gap-3">
        {isMobile ? (
          // Mobile: Show clickable logo
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <img
              src="https://res.cloudinary.com/dq4ihdqwt/image/upload/v1766574405/Untitled_design_2_jbpydg.png"
              alt="Ecom Factory"
              className="w-8 h-8"
            />
            <span className="text-base font-bold text-[#1E1B4B] font-['Manrope']">
              {t('app.name')}
            </span>
          </button>
        ) : (
          // Desktop: Show breadcrumbs
          <Breadcrumbs productName={productName} />
        )}
      </div>

      {/* Right: Language, Notifications & User (desktop) or Notifications & Menu (mobile) */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Language Selector - Desktop only */}
        {!isMobile && (
          <LanguageSelector size="sm" className="hidden md:inline-flex" />
        )}

        {/* Sound Toggle - Desktop only */}
        {!isMobile && (
          <button
            onClick={toggleSound}
            className={cn(
              'p-2 rounded-lg transition-colors',
              soundEnabled 
                ? 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100' 
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            )}
            title={soundEnabled ? 'Sound notifications on' : 'Sound notifications off'}
            data-testid="sound-toggle"
          >
            {soundEnabled ? (
              <Volume2 className="w-5 h-5" />
            ) : (
              <VolumeX className="w-5 h-5" />
            )}
          </button>
        )}

        {/* Notifications */}
        <div className="relative" ref={panelRef}>
          <button
            onClick={() => {
              if (isMobile) {
                navigate('/notifications');
              } else {
                setShowNotifications(!showNotifications);
              }
            }}
            className={cn(
              'p-2 rounded-lg transition-colors relative',
              showNotifications 
                ? 'bg-cyan-50 text-cyan-600' 
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            )}
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Panel - Desktop only */}
          {showNotifications && !isMobile && (
            <div 
              className={cn(
                'absolute top-full mt-2 bg-white rounded-xl border border-slate-200',
                'shadow-xl overflow-hidden z-50',
                // Responsive positioning
                'right-0 w-[calc(100vw-2rem)] max-w-[384px]',
                'md:right-0 md:w-96'
              )}
              data-testid="notifications-panel"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50">
                <h3 className="font-semibold text-[#1E1B4B]">
                  {t('notifications.title')}
                </h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="p-1.5 text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                      title="Mark all as read"
                    >
                      <CheckCheck className="w-4 h-4" />
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      onClick={clearAll}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Clear all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="max-h-[60vh] md:max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">
                      {t('notifications.emptyTitle')}
                    </p>
                    <p className="text-slate-400 text-xs mt-1">
                      {t('notifications.emptySubtitle')}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {notifications.map((notification) => {
                      const Icon = getNotificationIcon(notification.phase);
                      const colorClass = getNotificationColor(notification.phase);
                      
                      return (
                        <div
                          key={notification.id}
                          onClick={() => !notification.read && markAsRead(notification.id)}
                          className={cn(
                            'p-4 hover:bg-slate-50 cursor-pointer transition-colors',
                            !notification.read && 'bg-cyan-50/50'
                          )}
                        >
                          <div className="flex gap-3">
                            <div className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                              colorClass
                            )}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={cn(
                                  'text-sm',
                                  !notification.read ? 'font-semibold text-[#1E1B4B]' : 'font-medium text-slate-700'
                                )}>
                                  {notification.title}
                                </p>
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-cyan-500 rounded-full flex-shrink-0 mt-1.5" />
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="p-3 border-t border-slate-100 bg-slate-50">
                  <p className="text-xs text-slate-500 text-center">
                    {unreadCount > 0 
                      ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
                      : 'All caught up!'
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Avatar - Desktop only */}
        {!isMobile && (
          <div 
            className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center"
            title={user?.email}
            data-testid="user-avatar"
          >
            <span className="text-sm font-bold text-white">
              {getInitials()}
            </span>
          </div>
        )}

        {/* Hamburger Menu - Mobile only */}
        {isMobile && (
          <button
            onClick={onMenuClick}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
        )}
      </div>
    </header>
  );
}
